/**
 * Frontend Category Configuration
 *
 * Central configuration for all stock categories. This is the single source of truth
 * for category definitions in the frontend.
 *
 * To add/remove/reorder categories:
 * 1. Edit the CATEGORIES array below
 * 2. Update the matching backend config (backend/src/config/categoryConfig.ts)
 * 3. Run the sync validation script to ensure consistency
 *
 * IMPORTANT: This config must be kept in sync with the backend config.
 * The core fields (slug, displayName, isPremium, isDefault, priority) must match exactly.
 */

/**
 * Category definition with all metadata (frontend + backend)
 */
export interface CategoryDefinition {
  /** URL-safe category slug (used in routes like /:category) */
  slug: string;

  /** Human-readable display name */
  displayName: string;

  /** Database column name (e.g., 'is_spy', 'is_tech') - mirrors backend */
  dbColumn: string;

  /** Brief description of the category */
  description: string;

  /** Whether this category requires premium access */
  isPremium: boolean;

  /** Whether this is the default category (only one should be true) */
  isDefault: boolean;

  /** Display order/priority (lower numbers appear first) */
  priority: number;

  /**
   * Allow all stocks to be used for guessing while keeping daily selection limited
   * to the category's selection pool (e.g., popular = 80 curated stocks)
   */
  allowAllStocksForGuessing?: boolean;

  // Frontend-specific fields
  /** Tailwind color class or hex code for UI theming */
  color: string;

  /** Optional custom text color (overrides auto-calculated contrast color) */
  textColor?: string;

  /** Emoji or icon identifier for visual representation */
  icon: string;

  /** Extended SEO-optimized description for meta tags */
  seoDescription: string;

  /** SEO keywords for meta tags */
  seoKeywords: string[];

  /** Hero section heading for category page */
  heroHeading: string;

  /** Short name for compact UI displays (e.g., subheader buttons) */
  shortName?: string;
}

/**
 * Master category configuration array
 *
 * IMPORTANT: Array order determines display order in navigation and routing priority.
 * The first category with isDefault: true will be the default landing category.
 *
 * MUST MATCH: backend/src/config/categoryConfig.ts (except for frontend-specific fields)
 */
export const CATEGORIES: CategoryDefinition[] = [
  // ===== ACTIVE CATEGORIES (9 total: 3 free, 6 premium) =====
  {
    slug: 'popular',
    displayName: 'Household Name',
    shortName: 'Household',
    dbColumn: 'is_popular',
    description: 'Popular and well-known companies that dominate headlines and investor portfolios.',
    isPremium: false,
    isDefault: true,
    priority: 1,
    allowAllStocksForGuessing: true,
    color: '#1cbbbbff', // AZURE
    icon: '⭐',
    seoDescription: 'Play the Household Name stock wordle! This guess the stock game features 80 popular companies like Apple (AAPL). Daily stock challenge with ticker guessing for the most recognizable brands in investing.',
    seoKeywords: ['stock wordle', 'guess the stock game', 'popular stocks', 'blue chip stocks', 'ticker guessing', 'famous companies game', 'brand recognition', 'investing puzzle', 'wallstreetle popular'],
    heroHeading: 'Wallstreetle Household Name Challenge'
  },
  {
    slug: 'tech',
    displayName: 'Technology',
    shortName: 'Tech',
    dbColumn: 'is_tech',
    description: 'Explore digital trailblazers building platforms, software, silicon, and cloud ecosystems that shape how businesses and consumers operate every day.',
    isPremium: false,
    isDefault: false,
    priority: 2,
    color: '#8B5CF6', // violet-500
    icon: '💻',
    seoDescription: 'Play the finance wordle for tech stocks! This stock guessing game features 94 cutting-edge tech innovators like Microsoft (MSFT). Join the daily stock challenge with ticker guessing for cloud, semiconductors, and consumer electronics.',
    seoKeywords: ['finance wordle', 'stock guessing game', 'ticker guessing', 'tech stocks', 'technology investing game', 'equity game', 'stock trivia', 'nasdaq trivia', 'software stocks', 'wallstreetle tech'],
    heroHeading: 'Wallstreetle Tech Sector Puzzle'
  },
  {
    slug: 'wallstreetbets',
    displayName: 'WallStreetBets',
    shortName: 'WSB',
    dbColumn: 'is_wsb',
    description: 'Think you can hang with the apes? Guess which stocks are the most hyped on r/WallStreetBets.',
    isPremium: false,
    isDefault: false,
    priority: 3,
    color: '#8b8fc4ff',
    icon: '🚀',
    seoDescription: 'Play the WallStreetBets stock guessing game! This guess the stock game tracks the top 50 most-mentioned tickers from r/WallStreetBets. Daily stock challenge with ticker guessing for high-risk meme stocks like GameStop (GME).',
    seoKeywords: ['stock guessing game', 'guess the stock game', 'daily stock challenge', 'ticker guessing', 'wallstreetbets game', 'meme stock tracker', 'stock trivia', 'retail trader trends', 'equity game', 'wallstreetle wsb'],
    heroHeading: 'Wallstreetle WallStreetBets Challenge'
  },
  {
    slug: 'financial',
    displayName: 'Financial Services',
    shortName: 'Finance',
    dbColumn: 'is_financial',
    description: 'Follow institutions steering capital flows through banking, insurance, asset management, and fintech services powering global commerce.',
    isPremium: true,
    isDefault: false,
    priority: 4,
    color: '#10B981', // emerald-500
    icon: '🏦',
    seoDescription: 'Play the financial finance wordle! This equity game features 74 banking and finance firms like JPMorgan Chase (JPM). Investing puzzle with ticker guessing for retail banks, asset managers, and insurers.',
    seoKeywords: ['finance wordle', 'equity game', 'investing puzzle', 'ticker guessing', 'bank stocks game', 'financial sector puzzle', 'stock trivia', 'finance stocks', 'banking trivia', 'investment firms'],
    heroHeading: 'Wallstreetle Financial Services Puzzle'
  },
  {
    slug: 'consumer',
    displayName: 'Consumer Goods',
    shortName: 'Consumer',
    dbColumn: 'is_consumer',
    description: 'Review brands delivering household staples, discretionary luxuries, and experiential retail that mirror shifting shopper preferences.',
    isPremium: true,
    isDefault: false,
    priority: 5,
    color: '#F59E0B', // amber-500
    textColor: '#ffffff', // Force white text instead of auto-calculated black
    icon: '🛒',
    seoDescription: 'Play the consumer stock guessing game! This equity game features 87 consumer brands and retailers like Nike (NKE). Guess the stock game with ticker guessing for staples, discretionary goods, and service leaders.',
    seoKeywords: ['stock guessing game', 'equity game', 'guess the stock game', 'ticker guessing', 'consumer stocks', 'retail stocks game', 'stock trivia', 'brand recognition game', 'consumer goods trivia', 'retail investing'],
    heroHeading: 'Wallstreetle Consumer Brands Challenge'
  },
  {
    slug: 'pharma',
    displayName: 'Pharmaceuticals',
    shortName: 'Pharma',
    dbColumn: 'is_pharma',
    description: 'Inspect research-driven biopharma innovators turning clinical pipelines into lifesaving therapies while navigating regulatory and patent milestones.',
    isPremium: true,
    isDefault: false,
    priority: 6,
    color: '#EF4444', // red-500
    icon: '💊',
    seoDescription: 'Master the pharma stock wordle! This investing puzzle features 60 healthcare and pharma giants like Pfizer (PFE). Daily stock challenge with drug developers, biotech innovators, and medical suppliers through ticker guessing.',
    seoKeywords: ['stock wordle', 'investing puzzle', 'pharma stocks game', 'ticker guessing', 'healthcare stocks', 'biotech game', 'daily stock challenge', 'pharmaceutical trivia', 'healthcare investing'],
    heroHeading: 'Wallstreetle Pharma & Healthcare Challenge'
  },
  {
    slug: 'energy',
    displayName: 'Energy',
    dbColumn: 'is_energy',
    description: 'Analyze producers, refiners, and grid operators balancing hydrocarbons with renewables to keep transportation and utilities running.',
    isPremium: true,
    isDefault: false,
    priority: 7,
    color: '#064e3b', // emerald-900
    icon: '⚡',
    seoDescription: 'Play the energy finance wordle! This daily stock challenge features 53 energy and utility players like ExxonMobil (XOM). Investing puzzle with ticker guessing for oil majors, renewables, and grid operators.',
    seoKeywords: ['finance wordle', 'daily stock challenge', 'investing puzzle', 'ticker guessing', 'energy stocks game', 'oil and gas trivia', 'renewable energy', 'utility stocks', 'energy sector puzzle'],
    heroHeading: 'Wallstreetle Energy & Utilities Challenge'
  },
  {
    slug: 'industrial',
    displayName: 'Industrial',
    dbColumn: 'is_industrial',
    description: 'Survey manufacturers and logistics specialists optimizing supply chains, heavy equipment, aerospace systems, and infrastructure projects.',
    isPremium: true,
    isDefault: false,
    priority: 8,
    color: '#6B7280', // gray-500
    icon: '🏭',
    seoDescription: 'Play the industrial stock wordle! This guess the stock game features 105 industrial powerhouses like Caterpillar (CAT). Equity game with ticker guessing for manufacturing, logistics, and infrastructure champions.',
    seoKeywords: ['stock wordle', 'guess the stock game', 'equity game', 'ticker guessing', 'industrial stocks', 'manufacturing game', 'stock trivia', 'infrastructure stocks', 'construction companies', 'transportation stocks'],
    heroHeading: 'Wallstreetle Industrial Sector Puzzle'
  },
  {
    slug: 'foreign',
    displayName: 'Foreign',
    shortName: 'Global',
    dbColumn: 'is_foreign',
    description: 'Discover international companies trading as American Depositary Receipts, bringing global growth and diversification to U.S. markets.',
    isPremium: true,
    isDefault: false,
    priority: 9,
    color: '#6366F1', // indigo-500
    icon: '🌏',
    seoDescription: 'Play the foreign stock wordle! This daily stock challenge features 98 non-US stocks like Alibaba (BABA). Investing puzzle with ticker guessing for international companies from Asia, Europe, and Latin America.',
    seoKeywords: ['stock wordle', 'daily stock challenge', 'investing puzzle', 'ticker guessing', 'international stocks', 'adr stocks game', 'foreign stocks puzzle', 'equity game', 'global investing', 'emerging markets'],
    heroHeading: 'Wallstreetle Foreign Stocks Challenge'
  }

  // ===== COMMENTED OUT CATEGORIES (Can be restored anytime) =====
  // {
  //   slug: 'nasdaq',
  //   displayName: 'NASDAQ 100',
  //   dbColumn: 'is_nd100',
  //   description: 'The 100 largest non-financial companies on the NASDAQ stock exchange, from tech giants to biotech leaders.',
  //   isPremium: false,
  //   isDefault: false,
  //   priority: 10,
  //   color: '#00C805', // NASDAQ green
  //   icon: '💎',
  //   seoDescription: 'Play the stock wordle for NASDAQ 100! This guess the stock game features the largest non-financial companies like Tesla (TSLA) on the NASDAQ stock exchange. Daily stock challenge with tech giants and biotech leaders.',
  //   seoKeywords: ['stock wordle', 'nasdaq 100 game', 'guess the stock game', 'ticker guessing', 'nasdaq stocks', 'tech stocks game', 'daily stock challenge', 'nasdaq trivia', 'wallstreetle nasdaq'],
  //   heroHeading: 'Wallstreetle NASDAQ 100 Challenge'
  // },
  // {
  //   slug: 'hardware',
  //   displayName: 'Hardware',
  //   dbColumn: 'is_hardware',
  //   description: 'Evaluate leading tech and telecom innovators powering chips, devices, networks, and cloud infrastructure.',
  //   isPremium: false,
  //   isDefault: false,
  //   priority: 11,
  //   color: '#64748B', // slate-500
  //   icon: '🔌',
  //   seoDescription: 'Play the hardware stock wordle! This guess the stock game features 44 semiconductor makers like NVIDIA (NVDA). Daily stock challenge with ticker guessing for chips, devices, and infrastructure players.',
  //   seoKeywords: ['stock wordle', 'guess the stock game', 'daily stock challenge', 'ticker guessing', 'hardware stocks', 'semiconductor companies game', 'chip manufacturers puzzle', 'stock trivia', 'tech hardware', 'wallstreetle hardware'],
  //   heroHeading: 'Wallstreetle Hardware & Semiconductors Challenge'
  // },
  // {
  //   slug: 'software',
  //   displayName: 'Internet & Software',
  //   dbColumn: 'is_software',
  //   description: 'Explore software, cloud, and media powerhouses like Microsoft and Netflix driving the modern digital economy.',
  //   isPremium: false,
  //   isDefault: false,
  //   priority: 12,
  //   color: '#0EA5E9', // sky-500
  //   icon: '💿',
  //   seoDescription: 'Play the software stock guessing game! This stock wordle features 49 SaaS leaders like Salesforce (CRM). Guess the stock game with ticker guessing for subscription and enterprise platform giants.',
  //   seoKeywords: ['stock guessing game', 'stock wordle', 'guess the stock game', 'ticker guessing', 'software stocks', 'saas companies game', 'equity game', 'cloud computing puzzle', 'enterprise software', 'wallstreetle software'],
  //   heroHeading: 'Wallstreetle Internet & Software Challenge'
  // }
];

// =============================================================================
// DERIVED CONSTANTS (auto-generated from CATEGORIES array)
// =============================================================================

/**
 * TypeScript type union of all category slugs
 * Generated at runtime but used as const for type inference
 */
export type CategorySlug = typeof CATEGORIES[number]['slug'];

/**
 * All category slugs as array (in priority order)
 */
export const ALL_CATEGORIES: readonly CategorySlug[] = CATEGORIES.map(c => c.slug) as CategorySlug[];

/**
 * Premium category slugs only
 */
export const PREMIUM_CATEGORIES: readonly CategorySlug[] = CATEGORIES
  .filter(c => c.isPremium)
  .map(c => c.slug) as CategorySlug[];

/**
 * Free (non-premium) category slugs
 */
export const FREE_CATEGORIES: readonly CategorySlug[] = CATEGORIES
  .filter(c => !c.isPremium)
  .map(c => c.slug) as CategorySlug[];

/**
 * Default category slug
 */
export const DEFAULT_CATEGORY: CategorySlug = CATEGORIES.find(c => c.isDefault)?.slug as CategorySlug || 'popular';

/**
 * Map of category slug to database column name
 */
export const CATEGORY_DB_COLUMNS: Readonly<Record<CategorySlug, string>> = Object.freeze(
  CATEGORIES.reduce((acc, c) => {
    acc[c.slug as CategorySlug] = c.dbColumn;
    return acc;
  }, {} as Record<CategorySlug, string>)
);

/**
 * Map of category slug to display name
 */
export const CATEGORY_DISPLAY_NAMES: Readonly<Record<CategorySlug, string>> = Object.freeze(
  CATEGORIES.reduce((acc, c) => {
    acc[c.slug as CategorySlug] = c.displayName;
    return acc;
  }, {} as Record<CategorySlug, string>)
);

/**
 * Map of category slug to description
 */
export const CATEGORY_DESCRIPTIONS: Readonly<Record<CategorySlug, string>> = Object.freeze(
  CATEGORIES.reduce((acc, c) => {
    acc[c.slug as CategorySlug] = c.description;
    return acc;
  }, {} as Record<CategorySlug, string>)
);

/**
 * Map of category slug to color
 */
export const CATEGORY_COLORS: Readonly<Record<CategorySlug, string>> = Object.freeze(
  CATEGORIES.reduce((acc, c) => {
    acc[c.slug as CategorySlug] = c.color;
    return acc;
  }, {} as Record<CategorySlug, string>)
);

/**
 * Map of category slug to icon
 */
export const CATEGORY_ICONS: Readonly<Record<CategorySlug, string>> = Object.freeze(
  CATEGORIES.reduce((acc, c) => {
    acc[c.slug as CategorySlug] = c.icon;
    return acc;
  }, {} as Record<CategorySlug, string>)
);

/**
 * Map of category slug to SEO description
 */
export const CATEGORY_SEO_DESCRIPTIONS: Readonly<Record<CategorySlug, string>> = Object.freeze(
  CATEGORIES.reduce((acc, c) => {
    acc[c.slug as CategorySlug] = c.seoDescription;
    return acc;
  }, {} as Record<CategorySlug, string>)
);

/**
 * Map of category slug to SEO keywords
 */
export const CATEGORY_SEO_KEYWORDS: Readonly<Record<CategorySlug, string[]>> = Object.freeze(
  CATEGORIES.reduce((acc, c) => {
    acc[c.slug as CategorySlug] = c.seoKeywords;
    return acc;
  }, {} as Record<CategorySlug, string[]>)
);

/**
 * Map of category slug to hero heading
 */
export const CATEGORY_HERO_HEADINGS: Readonly<Record<CategorySlug, string>> = Object.freeze(
  CATEGORIES.reduce((acc, c) => {
    acc[c.slug as CategorySlug] = c.heroHeading;
    return acc;
  }, {} as Record<CategorySlug, string>)
);

/**
 * Map of database column to category slug (reverse lookup)
 */
export const DB_COLUMN_TO_CATEGORY: Readonly<Record<string, CategorySlug>> = Object.freeze(
  CATEGORIES.reduce((acc, c) => {
    acc[c.dbColumn] = c.slug as CategorySlug;
    return acc;
  }, {} as Record<string, CategorySlug>)
);

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Get full category configuration by slug
 */
export function getCategoryConfig(slug: string): CategoryDefinition | undefined {
  return CATEGORIES.find(c => c.slug === slug);
}

/**
 * Check if a category slug is valid
 */
export function isValidCategory(slug: string): slug is CategorySlug {
  return CATEGORIES.some(c => c.slug === slug);
}

/**
 * Check if a category requires premium access
 */
export function isPremiumCategory(slug: string): boolean {
  const category = getCategoryConfig(slug);
  return category?.isPremium ?? false;
}

/**
 * Check if a category is free (non-premium)
 */
export function isFreeCategory(slug: string): boolean {
  return !isPremiumCategory(slug);
}

/**
 * Get category display name
 */
export function getCategoryDisplayName(slug: string): string {
  return CATEGORY_DISPLAY_NAMES[slug as CategorySlug] || slug;
}

/**
 * Get category color
 */
export function getCategoryColor(slug: string): string {
  return CATEGORY_COLORS[slug as CategorySlug] || '#3B82F6';
}

/**
 * Get category icon
 */
export function getCategoryIcon(slug: string): string {
  return CATEGORY_ICONS[slug as CategorySlug] || '📊';
}

/**
 * Get category database column name
 */
export function getCategoryDbColumn(slug: string): string | undefined {
  return CATEGORY_DB_COLUMNS[slug as CategorySlug];
}

/**
 * Get category slug from database column name
 */
export function getCategoryFromDbColumn(dbColumn: string): CategorySlug | undefined {
  return DB_COLUMN_TO_CATEGORY[dbColumn];
}

/**
 * Get all categories with their full configuration
 */
export function getAllCategoryConfigs(): readonly CategoryDefinition[] {
  return CATEGORIES;
}

/**
 * Get categories filtered by premium status
 */
export function getCategoriesByPremiumStatus(isPremium: boolean): readonly CategoryDefinition[] {
  return CATEGORIES.filter(c => c.isPremium === isPremium);
}

/**
 * Get default category configuration
 */
export function getDefaultCategoryConfig(): CategoryDefinition {
  return CATEGORIES.find(c => c.isDefault) || CATEGORIES[0];
}

/**
 * Get category SEO metadata
 */
export function getCategorySeoMetadata(slug: string): {
  description: string;
  keywords: string[];
  heroHeading: string;
} | undefined {
  const category = getCategoryConfig(slug);
  if (!category) return undefined;

  return {
    description: category.seoDescription,
    keywords: category.seoKeywords,
    heroHeading: category.heroHeading
  };
}

/**
 * Validate that category configuration is correct
 * Throws error if validation fails
 */
export function validateCategoryConfiguration(): void {
  // Check for duplicate slugs
  const slugs = CATEGORIES.map(c => c.slug);
  const uniqueSlugs = new Set(slugs);
  if (slugs.length !== uniqueSlugs.size) {
    throw new Error('Duplicate category slugs found in configuration');
  }

  // Check for duplicate database columns
  const dbColumns = CATEGORIES.map(c => c.dbColumn);
  const uniqueDbColumns = new Set(dbColumns);
  if (dbColumns.length !== uniqueDbColumns.size) {
    throw new Error('Duplicate database columns found in configuration');
  }

  // Check for duplicate priorities
  const priorities = CATEGORIES.map(c => c.priority);
  const uniquePriorities = new Set(priorities);
  if (priorities.length !== uniquePriorities.size) {
    throw new Error('Duplicate priorities found in configuration');
  }

  // Check that exactly one default exists
  const defaults = CATEGORIES.filter(c => c.isDefault);
  if (defaults.length === 0) {
    throw new Error('No default category found in configuration');
  }
  if (defaults.length > 1) {
    throw new Error('Multiple default categories found in configuration');
  }

  // Check that all categories have non-empty fields
  for (const category of CATEGORIES) {
    if (!category.slug || !category.displayName || !category.dbColumn || !category.description ||
        !category.color || !category.icon || !category.seoDescription || !category.heroHeading) {
      throw new Error(`Category ${category.slug || 'unknown'} has empty required fields`);
    }
    if (!Array.isArray(category.seoKeywords) || category.seoKeywords.length === 0) {
      throw new Error(`Category ${category.slug} has no SEO keywords`);
    }
  }
}

// Run validation on module load (will throw if config is invalid)
validateCategoryConfiguration();
